<template>
  <div class="creation-demand-step-pricing-boost">
    <VeeForm ref="form" @submit="save">
      <!-- Only show boost section if NOT interim contract -->
      <template v-if="!isInterim">
        <h2 v-if="boostPercentages.length" class="step-title-optional">
          {{ $filters.whitespacedPunctuation($t("ETAB.PAGES.DEMANDS.CREATION.PRICING_BOOST.@TITLE")) }}
        </h2>

        <section v-if="boostPercentages.length" class="c-boost-pricing q-pa-md">
          <p class="text-left">
            {{ stepAssetsModel.getDescription() }}
          </p>
          <ul class="c-boost-pricing__list">
            <li
              v-for="(boost, index) in boostPercentages"
              :key="index"
              :class="{
                'boost-percentage--selected': selectedBoost === boost.amount,
                'boost-percentage--with-label': !!boost.label,
              }"
              class="boost-percentage"
              @click="toggleBoost(boost.amount)"
            >
              <strong>{{ $t("ETAB.PAGES.DEMANDS.CREATION.PRICING_BOOST.@PERCENTAGE", { percentage: boost.amount }) }}</strong>
              <strong v-if="boost.label" class="boost-percentage__label">{{ boost.label }}</strong>
            </li>
          </ul>
          <Field
            v-slot="{ errors, field, value }"
            v-model.number="selectedBoost"
            tag="div"
            :name="$filters.whitespacedPunctuation($t('ETAB.PAGES.DEMANDS.CREATION.PRICING_BOOST.@CUSTOM_INPUT_LABEL'))"
            :rules="{ max_value: 499, min_value: 0, integer: true }"
          >
            <div :class="{ 'c-boost-pricing-custom--selected': isCustomBoost }" class="c-boost-pricing__custom c-boost-pricing-custom">
              <span>{{ $t("ETAB.PAGES.DEMANDS.CREATION.PRICING_BOOST.@CUSTOM_INPUT_LABEL") }}</span>
              <span class="c-boost-pricing-custom-input">
                <QInput
                  v-bind="field"
                  :model-value="value"
                  type="text"
                  mask="###"
                  :debounce="300"
                  dense
                  borderless
                  :input-class="{
                    'c-boost-pricing-custom-input__field': true,
                    'c-boost-pricing-custom-input__field--selected': isCustomBoost,
                  }"
                  @update:model-value="updateBoostPercentage"
                />

                <span>%</span>
              </span>
            </div>
            <p v-if="errors.length" class="q-mt-sm q-mb-none">
              <QIcon name="fad fa-circle-exclamation" color="red" class="q-mr-sm" />
              {{ $t("ETAB.PAGES.DEMANDS.CREATION.PRICING_BOOST.@CUSTOM_INPUT_ERROR") }}
            </p>
          </Field>
        </section>
      </template>

      <!-- Pricing component (works for both AE and interim) -->
      <PricingComponent :pricing-boost="stepAssetsModel" :boost-percentage="boostPercentage" :is-interim="isInterim" />

      <div class="pricing-helper">
        <span class="text-right q-my-md flex justify-end">
          *
          <a class="text-underline cursor-pointer" @click="openModal">
            {{ $t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_PRICE_HELPER") }}
          </a>
        </span>

        <p class="asterisk">
          {{
            isInterim ? $t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_PRICE_ASTERISK_INTERIM") : $t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_PRICE_ASTERISK")
          }}
        </p>
      </div>
      <transition appear enter-active-class="animated fadeIn slower delay-1s" leave-active-class="animated fadeOut">
        <section class="q-footer q-layout__section--marginal fixed-bottom">
          <div>
            <QBtn
              color="secondary"
              class="full-width btn-navigation"
              :label="$t('ETAB.BUTTONS.@NEXT')"
              no-caps
              type="submit"
              :loading="loading"
              :disabled="loading"
            />
          </div>
        </section>
      </transition>
    </VeeForm>
    <PricingModal v-model:opened="modalOpened" :is-interim="isInterim" />
  </div>
</template>

<script>
  import { CreationStepPricingBoostModel, ICreationStepPricingBoost } from "@core/models";
  import { Form as VeeForm, Field } from "vee-validate";
  import PricingComponent from "@etab/components/creation/PricingComponent.component.vue";
  import PricingModal from "@etab/components/creation/Pricing.modal.vue";

  export default {
    components: {
      Field,
      PricingComponent,
      PricingModal,
      VeeForm,
    },
    props: {
      stepAssets: {
        type: Object,
        default: () => ({}),
      },
      stepData: {
        type: Object,
        default: () => ({}),
      },
      loading: {
        type: Boolean,
        default: false,
      },
      isInterim: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["saveStep"],
    data() {
      return {
        submitting: false,
        modalOpened: false,
        selectedBoost: 0,
        boostPercentage: 0,
      };
    },
    computed: {
      stepAssetsModel() {
        return new CreationStepPricingBoostModel(this.stepAssets);
      },
      boostPercentages() {
        return this.stepAssetsModel.getBoostPercentagesList();
      },
      qualification() {
        return this.stepAssetsModel.getQualification();
      },
      totalExtra() {
        return this.stepAssetsModel.getTotalExtra();
      },
      isCustomBoost() {
        return !this.boostPercentages.find((boost) => boost.amount === this.boostPercentage) && this.boostPercentage !== 0 && !!this.boostPercentage;
      },
    },
    created() {
      // For interim contracts, don't set boost percentage
      if (!this.isInterim) {
        this.selectedBoost = this.stepData?.boost_percentage ?? 0;
        this.boostPercentage = this.stepData?.boost_percentage ?? 0;
      }
    },
    methods: {
      toggleBoost(boost = 0) {
        if (this.selectedBoost === boost) {
          this.selectedBoost = 0;
          this.boostPercentage = 0;
        } else {
          this.selectedBoost = boost;
          this.boostPercentage = boost;
        }
      },
      updateBoostPercentage(boost) {
        this.boostPercentage = Number(boost);
      },
      openModal() {
        this.modalOpened = true;
      },
      async save() {
        const valid = await this.$refs.form.validate();
        if (valid) {
          // For interim contracts, don't include boost percentage
          const data =
            !this.isInterim && Number(this.boostPercentage) > 0 ? { [ICreationStepPricingBoost.boostPercentage]: Number(this.boostPercentage) } : {};
          this.$emit("saveStep", data);
        }
      },
    },
  };
</script>

<style lang="scss">
  .creation-demand-step-pricing-boost {
    margin-bottom: 80px;
    .c-boost-pricing {
      display: flex;
      flex-direction: column;
      border-radius: 4px;
      box-shadow: $card-shadow;
      background: white;
      &__list {
        display: flex;
        align-items: center;
        height: 42px;
        margin: 0.5rem 0;
        padding: 0;
        @media screen and (max-width: $breakpoint-sm) {
          height: 34px;
        }
        > li {
          margin: 0 3px;
          &:first-child {
            margin-left: 0;
          }
          &:last-child {
            margin-right: 0;
          }
        }

        .boost-percentage {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          font-size: 12px;
          border-radius: 8px;
          padding: 18px 8px;
          color: $primary;
          background-color: rgba(7, 44, 85, 0.15);
          cursor: pointer;
          height: 100%;
          width: auto;
          &--selected {
            border: 1px solid $secondary;
            background-color: rgba(255, 44, 85, 0.15);
          }
          &--with-label {
            flex: 2;
          }
          &__label {
            font-size: 10px;
            color: $secondary;
            @media screen and (max-width: $breakpoint-sm) {
              font-size: 8px;
            }
          }
        }
      }
      &__custom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-radius: 8px;
        padding: 0 1rem;
        background-color: rgba(7, 44, 85, 0.15);
        height: 50px;
      }
    }

    .c-boost-pricing-custom {
      &--selected {
        border: 1px solid $secondary;
        background-color: rgba(255, 44, 85, 0.15);
      }
    }

    .c-boost-pricing-custom-input {
      display: flex;
      align-items: baseline;
      align-self: end;

      border: 1px solid transparent;
      > span {
        width: 20px;
        text-align: right;
      }

      &__field {
        width: 50px;
        height: 30px;
        border-radius: 8px;
        border: 1px solid transparent;
        text-align: center;
        background-color: white;
        &--selected {
          border: 1px solid $secondary;
        }
      }
    }
  }
</style>
